import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X, Check, ShieldCheck, BarChart3, ChevronDown } from "lucide-react";
import { getConsent, setConsent } from "@/lib/consent";

/**
 * CookieConsent
 * ─────────────
 * GDPR-friendly banner that appears once, on first visit, until the user
 * chooses. Two primary buckets:
 *   • "necessary"   - always on (session id, theme, auth). Not toggleable.
 *   • "analytics"   - off by default; enables pageview / event tracking
 *                     which is otherwise silently skipped by /lib/analytics.js.
 *
 * Actions:
 *   • Akceptuj wszystko → analytics: true
 *   • Tylko niezbędne   → analytics: false (session id + auth still work)
 *   • Dostosuj          → expandable panel with per-bucket switches
 *
 * The banner does NOT block scrolling / interaction - it's a bottom-of-screen
 * card that dismisses itself once the user chooses.
 */
export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    useEffect(() => {
        const c = getConsent();
        if (!c.decided) {
            // Small delay so it doesn't clash with the hero animation.
            const t = setTimeout(() => setVisible(true), 1200);
            return () => clearTimeout(t);
        }
        setAnalytics(!!c.analytics);
        return undefined;
    }, []);

    const acceptAll = () => {
        setConsent({ analytics: true });
        setAnalytics(true);
        setVisible(false);
    };

    const acceptNecessary = () => {
        setConsent({ analytics: false });
        setAnalytics(false);
        setVisible(false);
    };

    const saveCustom = () => {
        setConsent({ analytics });
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="cookie-consent"
                    data-testid="cookie-consent"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ type: "spring", stiffness: 220, damping: 24 }}
                    className="fixed z-[75] left-3 right-3 bottom-3 md:left-auto md:right-5 md:bottom-5 md:max-w-lg rounded-lg border border-[color:var(--border-strong)] bg-[color:var(--bg-surface)] shadow-2xl overflow-hidden"
                    style={{
                        boxShadow:
                            "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(250,204,21,0.15)",
                    }}
                    role="dialog"
                    aria-labelledby="cookie-consent-title"
                >
                    <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full blur-3xl pointer-events-none bg-[color:var(--primary)]/20" />
                    <div className="relative p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--primary)]/15 text-[color:var(--primary)] shrink-0">
                                <Cookie size={18} strokeWidth={1.8} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div
                                    id="cookie-consent-title"
                                    className="font-display uppercase tracking-wider text-white text-sm mb-1"
                                >
                                    Prywatność i statystyki
                                </div>
                                <div className="text-xs text-[color:var(--text-secondary)] leading-relaxed">
                                    Używamy niezbędnego lokalnego storage (login,
                                    motyw). Opcjonalnie zbieramy anonimowe
                                    statystyki odwiedzin, żeby wiedzieć co
                                    ulepszyć - Twoja decyzja.
                                </div>
                            </div>
                            <button
                                onClick={acceptNecessary}
                                aria-label="Zamknij"
                                className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-[color:var(--text-secondary)] hover:text-white hover:bg-white/10 transition"
                                data-testid="cookie-consent-close"
                            >
                                <X size={13} />
                            </button>
                        </div>

                        {expanded && (
                            <div className="mt-3 space-y-2 pt-3 border-t border-white/10">
                                <ConsentRow
                                    icon={ShieldCheck}
                                    title="Niezbędne"
                                    subtitle="Login, motyw, ID sesji - wymagane."
                                    locked
                                    checked
                                />
                                <ConsentRow
                                    icon={BarChart3}
                                    title="Statystyki (analytics)"
                                    subtitle="Anonimowe wyświetlenia stron, kraje, urządzenia. Bez cookies od stron trzecich."
                                    checked={analytics}
                                    onChange={setAnalytics}
                                    testid="cookie-toggle-analytics"
                                />
                            </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                            {!expanded && (
                                <button
                                    onClick={() => setExpanded(true)}
                                    className="btn-ghost text-[11px] uppercase tracking-[0.15em] shrink-0"
                                    data-testid="cookie-consent-customize"
                                >
                                    <ChevronDown size={12} />
                                    Dostosuj
                                </button>
                            )}
                            {expanded ? (
                                <button
                                    onClick={saveCustom}
                                    className="btn-primary text-xs"
                                    data-testid="cookie-consent-save"
                                >
                                    <Check size={12} />
                                    Zapisz wybór
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={acceptNecessary}
                                        className="btn-ghost text-xs shrink-0"
                                        data-testid="cookie-consent-necessary"
                                    >
                                        Tylko niezbędne
                                    </button>
                                    <button
                                        onClick={acceptAll}
                                        className="btn-primary text-xs ml-auto"
                                        data-testid="cookie-consent-accept-all"
                                    >
                                        <Check size={12} />
                                        Akceptuj wszystko
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function ConsentRow({ icon: Icon, title, subtitle, checked, onChange, locked, testid }) {
    return (
        <div className="flex items-start gap-3 p-2.5 rounded-md hover:bg-white/[0.03] transition">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-white/5 text-[color:var(--text-secondary)] shrink-0">
                <Icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white flex items-center gap-2">
                    {title}
                    {locked && (
                        <span className="text-[9px] px-1.5 py-[1px] rounded-full bg-white/5 text-[color:var(--text-muted)] uppercase tracking-[0.15em]">
                            Wymagane
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-[color:var(--text-secondary)] leading-relaxed">
                    {subtitle}
                </div>
            </div>
            {/* Toggle */}
            <button
                type="button"
                role="switch"
                aria-checked={!!checked}
                disabled={locked}
                onClick={() => !locked && onChange && onChange(!checked)}
                data-testid={testid}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                    checked ? "bg-[color:var(--primary)]" : "bg-white/15"
                } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        checked ? "translate-x-4" : ""
                    }`}
                />
            </button>
        </div>
    );
}
